import { useRef, useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { FormControl, InputLabel, MenuItem, Paper, Select } from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useSendMessageStore, useThreadsStore, useUsersStore } from '@lib';
import { Button } from '@widgets/Button';
import { Attachments, IconButton } from '@widgets';
import { Input } from '@shared';

const useStyles = makeStyles(() => ({
  container: {
    minWidth: '330px',
  },
  caption: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  post: {
    padding: '32px',
  },
  reply: {
    padding: '20px',
  },
  header: {
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: 1.33,
    marginTop: 0,
    marginBottom: '16px',
    textAlign: 'center',
  },
  form: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  controls: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '24px',
  },
  action: {},
  uploadField: {
    opacity: 0,
    position: 'absolute',
    zIndex: -1000,
  },
}));

interface SendMessageProps {
  className?: string;
  mode?: 'post' | 'reply';
  subject?: string;
  threadId?: string;
  receiverId?: string;
  canClose?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
}

export const SendMessage = ({ mode = 'post', ...props }: SendMessageProps) => {
  const { users: receivers, getUsers } = useUsersStore();
  const { loading } = useThreadsStore();
  const { sendMessage } = useSendMessageStore();

  const [receiverId, setReceiverId] = useState<string>(props.receiverId || '');
  const [subject, setSubject] = useState<string>(props.subject || '');
  const [message, setMessage] = useState<string>('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const refUpload = useRef<HTMLInputElement | null>(null);

  const styles = useStyles();

  useEffect(() => {
    getUsers();
  }, []);

  const sendMessageHandler = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage({ groupId: props.threadId, receiverId, subject, body: message, attachments });
    setSubject(props.subject || '');
    setMessage('');
    setAttachments([]);
    mode === 'post' && setReceiverId('');
    props.onSuccess?.();
  };

  const addAttachment = (value: File[]) => {
    if (!value?.length) return;
    setAttachments((source) => [...source, ...value]);
  };

  const removeAttachment = (index: number) => setAttachments((source) => (source.splice(index, 1), [...source]));

  const classMode = mode === 'post' ? styles.post : styles.reply;
  return (
    <Paper className={classNames(styles.container, classMode)}>
      {mode === 'post' ? <h3 className={styles.header}>New Message</h3> : null}
      <form className={styles.form} onSubmit={sendMessageHandler}>
        <div className={styles.controls}>
          {mode !== 'post' && props.canClose ? (
            <>
              <div className={styles.caption}>
                <span />
                <IconButton onClick={props.onClose} size='large'>
                  <CloseIcon />
                </IconButton>
              </div>
            </>
          ) : null}
          {mode === 'post' ? (
            <FormControl fullWidth margin='normal'>
              <InputLabel>Receiver</InputLabel>
              <Select
                variant='standard'
                name='receiverId'
                label='Receiver'
                fullWidth
                required
                value={receiverId || ''}
                onChange={(e) => setReceiverId(e.target.value)}
              >
                {receivers
                  ? receivers.map((item, i) => (
                      <MenuItem value={item.id} key={i}>
                        {[item.firstname, item.lastname].filter((item) => !!item).join(' ')}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
          ) : null}
          {mode === 'post' ? (
            <Input label='Subject' name='subject' value={subject || ''} onChange={(e) => setSubject(e.target.value)} required />
          ) : null}
          <Input
            label='Enter your message'
            name='message'
            value={message || ''}
            onChange={(e) => setMessage(e.target.value)}
            required
            multiline
            autoFocus={mode === 'reply'}
          />
        </div>
        {attachments && attachments.length ? <Attachments mode='upload' items={attachments} onDelete={removeAttachment} /> : null}
        <div className={styles.actions}>
          <input
            ref={refUpload}
            className={styles.uploadField}
            type='file'
            name='attachments'
            multiple
            onChange={(e) => addAttachment(Array.from(e.currentTarget.files || []))}
          />
          <Button type='button' startIcon={<AttachFile />} onClick={() => refUpload.current?.click()}>
            Attach
          </Button>
          <Button color='primary' type='submit' loading={loading}>
            {mode === 'reply' ? 'Reply' : 'Send'}
          </Button>
        </div>
      </form>
    </Paper>
  );
};
